import DigiobcanApi from '../api/DigiobcanApi';
import type { AdminReportListIssue } from '../api/DigiobcanApi';
import { useM } from '../lib/query';
import { useLogger } from '../providers/LoggerProvider';
import { useResponsive } from '../providers/ResponsiveProvider';
import { __, toastError, toastSuccess } from '../utils';
import { ReportStatusDot } from './AdminReportStatusItem';
import ConfirmationDialog from './ConfirmationDialog';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

/** Podnet vybratý v zozname spolu s nálezmi, ktoré prihlásený admin vidí. */
export type MergeReport = {
  reportId: string;
  title: string;
  issues: AdminReportListIssue[];
};

/**
 * Stavy nálezu, ktorý sa dá označiť ako duplikát. Zrkadlí kontrolu
 * `INVALID_DUPLICATE_ISSUE_STATUS` v API - vyriešený nález je uzavretý
 * finálnym stanoviskom a to sa občanovi neberie, presunutý ani už zlúčený
 * nález v kategórii nežije.
 */
const DUPLICABLE_STATUSES: AdminReportListIssue['status'][] = [
  'open',
  'accepted',
];

/**
 * Poradie, v akom sa spomedzi nálezov primárneho podnetu vyberá originál.
 *
 * Stav originálu API neobmedzuje, takže je to len otázka toho, ktorý z nich
 * dvojicu unesie najlepšie: prijatý sa rieši, vyriešený má stanovisko,
 * podaný ešte len čaká. Presunutý a už zlúčený sú posledné - u zlúčeného
 * navyše API vzťah skráti na jeho vlastný originál, teda na nález tretieho
 * podnetu, čo z tohto dialógu vidieť nie je.
 */
const PARENT_PREFERENCE: AdminReportListIssue['status'][] = [
  'accepted',
  'resolved',
  'open',
  'moved',
  'duplicate',
];

type Pair = {
  reportId: string;
  issue: AdminReportListIssue;
  /** Nález primárneho podnetu v tej istej kategórii - až ten sa dá uložiť. */
  parent?: AdminReportListIssue;
  reason?: 'notDuplicable' | 'noCategoryInPrimary';
};

/**
 * Duplicita je vzťah dvoch nálezov v **tej istej kategórii** - tak ju navrhuje
 * aj detektor v reports-processore a od tejto zmeny to vynucuje aj API
 * (`DUPLICATE_ISSUE_CATEGORY_MISMATCH`). Preto sa nespájajú podnety ako celky:
 * pre každý nález sa hľadá nález primárneho podnetu v jeho kategórii. Ten už
 * nemusí byť prijatý - originálom smie byť nález v ľubovoľnom stave.
 */
const pairsFor = (primary: MergeReport, others: MergeReport[]): Pair[] =>
  others.flatMap((report) =>
    report.issues.map((issue) => {
      if (!DUPLICABLE_STATUSES.includes(issue.status)) {
        return {
          reportId: report.reportId,
          issue,
          reason: 'notDuplicable' as const,
        };
      }

      const inCategory = primary.issues.filter(
        (candidate) => candidate.category_id === issue.category_id,
      );
      const parent = [...inCategory].sort(
        (a, b) =>
          PARENT_PREFERENCE.indexOf(a.status) -
          PARENT_PREFERENCE.indexOf(b.status),
      )[0];

      if (parent) {
        return { reportId: report.reportId, issue, parent };
      }

      return {
        reportId: report.reportId,
        issue,
        reason: 'noCategoryInPrimary' as const,
      };
    }),
  );

const REASON_LABELS: Record<NonNullable<Pair['reason']>, string> = {
  notDuplicable: __('nedotkne sa - nález je už vyriešený alebo uzavretý'),
  noCategoryInPrimary: __('preskočí sa - primárny podnet túto kategóriu nemá'),
};

type Props = {
  /** Podnety vybraté v zozname; modal sa otvára až od dvoch. */
  reports: MergeReport[];
  onClose: () => void;
  onSuccess: () => void;
};

const AdminReportsMergeModal: React.FC<Props> = ({
  reports,
  onClose,
  onSuccess,
}) => {
  const { logger } = useLogger();
  const { isMd } = useResponsive();
  const [primaryId, setPrimaryId] = React.useState<string>();

  // Koľko párov by vzniklo, keby primárne bolo práve tento podnet. Slúži aj
  // na predvolbu - úradník tak nemusí kandidátov preklikávať naslepo.
  const candidates = React.useMemo(
    () =>
      reports.map((report) => {
        const pairs = pairsFor(
          report,
          reports.filter((other) => other.reportId !== report.reportId),
        );

        return {
          report,
          pairs,
          matched: pairs.filter((pair) => pair.parent).length,
          // Menovatel su len nalezy, ktore sa oznacit daju - vyrieseny,
          // presunuty ani uz zluceny nalez do pomeru nepatri, akcia sa ich
          // nedotkne.
          duplicable: pairs.filter((pair) => pair.reason !== 'notDuplicable')
            .length,
        };
      }),
    [reports],
  );

  React.useEffect(() => {
    if (!reports.length) {
      setPrimaryId(undefined);
      return;
    }

    const best = [...candidates].sort((a, b) => b.matched - a.matched)[0];
    setPrimaryId(best?.report.reportId);
  }, [candidates, reports.length]);

  const selected = candidates.find(
    (candidate) => candidate.report.reportId === primaryId,
  );
  const pairs = selected?.pairs ?? [];
  const savable = pairs.filter((pair) => pair.parent);

  const markAsDuplicatesMutation = useM(
    () =>
      Promise.all(
        savable.map((pair) =>
          DigiobcanApi.adminMarkIssueDuplicate(
            pair.parent!.issue_id,
            {},
            { duplicateId: pair.issue.issue_id },
          ),
        ),
      ),
    {
      onSuccess: () => {
        toastSuccess(
          savable.length === 1
            ? __('Nález bol označený ako duplikát.')
            : __('Nálezy boli označené ako duplikáty.'),
        );
        onSuccess();
        onClose();
      },
      onError: (e) => {
        logger.error('Error marking issues as duplicate', {
          action: 'markAsDuplicate',
          error: e,
          data: {
            primaryId,
            pairs: savable.map((pair) => ({
              duplicate: pair.issue.issue_id,
              parent: pair.parent?.issue_id,
            })),
          },
        });

        if (e.details.name === 'INVALID_DUPLICATE_ISSUE_STATUS') {
          toastError(
            __('Zlyhala zmena stavu - nález sa už duplikátom stať nemôže.'),
          );
        } else if (e.details.name === 'DUPLICATE_ISSUE_CATEGORY_MISMATCH') {
          toastError(
            __('Zlyhala zmena stavu - nálezy nie sú v tej istej kategórii.'),
          );
        } else {
          toastError(__('Niečo sa pokazilo.'));
        }
      },
    },
  );

  const categoryLabel = (issue: AdminReportListIssue) =>
    issue.category_name || issue.category_id;

  return (
    <ConfirmationDialog
      open={reports.length > 1}
      onClose={onClose}
      title={__('Označenie duplikátov')}
      confirmLabel={__('Uložiť')}
      onConfirm={() => markAsDuplicatesMutation.mutate(undefined)}
      confirmDisabled={!savable.length}
      isLoading={markAsDuplicatesMutation.isPending}
      fullWidth
      maxWidth='md'
    >
      {/* Dva stĺpce: vľavo voľba, vpravo jej dôsledok. Na mobile pod sebou -
          v jednom stĺpci by sa páry aj tak nedali čítať vedľa výberu. */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        gap={2}
        divider={
          <Divider flexItem orientation={isMd ? 'vertical' : 'horizontal'} />
        }
      >
        <Stack sx={{ flex: 1, minWidth: 0, gap: 1 }}>
          <Stack>
            <Typography variant='subtitle2'>
              {__('Ktorý podnet je primárny?')}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {__('Ostatné vybraté sa označia ako jeho duplikáty.')}
            </Typography>
          </Stack>

          <RadioGroup
            value={primaryId ?? ''}
            onChange={(e) => setPrimaryId(e.target.value)}
            sx={{ gap: 1 }}
          >
            {candidates.map(({ report, matched, duplicable }) => (
              <FormControlLabel
                key={report.reportId}
                value={report.reportId}
                control={<Radio size='small' />}
                disableTypography
                label={
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                      {report.reportId}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {report.title}
                    </Typography>
                    <Typography
                      variant='caption'
                      color={matched ? 'text.secondary' : 'error.dark'}
                    >
                      {matched
                        ? `${__('označí')} ${matched} ${__('z')} ${duplicable} ${__('zlúčiteľných nálezov')}`
                        : __('nespáruje žiadny nález')}
                    </Typography>
                  </Stack>
                }
                sx={{
                  m: 0,
                  p: 1,
                  alignItems: 'flex-start',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor:
                    report.reportId === primaryId ? 'primary.main' : 'divider',
                  backgroundColor:
                    report.reportId === primaryId
                      ? 'action.hover'
                      : 'transparent',
                }}
              />
            ))}
          </RadioGroup>
        </Stack>

        <Stack sx={{ flex: 1, minWidth: 0, gap: 1 }}>
          <Stack>
            <Typography variant='subtitle2'>
              {savable.length
                ? `${__('Uloží sa')} (${savable.length})`
                : __('Nie je čo uložiť')}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {__('Duplikát vzniká vždy v rámci jednej kategórie.')}
            </Typography>
          </Stack>

          {reports
            .filter((report) => report.reportId !== primaryId)
            .map((report) => (
              <Stack key={report.reportId} sx={{ gap: 0.25 }}>
                <Typography variant='caption' sx={{ fontWeight: 600 }}>
                  {report.reportId}
                </Typography>

                {pairs
                  .filter((pair) => pair.reportId === report.reportId)
                  .map((pair) => (
                    <Stack key={pair.issue.issue_id} sx={{ pl: 1 }}>
                      {/* `flexWrap` nie je kozmetika: bez neho by dlhy riadok
                          (kategoria + dve ID) rozsiril cely dialog nad sirku
                          obrazovky, lebo flex row sa pod min-content nezmensi. */}
                      <Stack
                        direction='row'
                        alignItems='center'
                        gap={0.5}
                        sx={{ flexWrap: 'wrap' }}
                      >
                        <ReportStatusDot status={pair.issue.status} size={8} />
                        <Typography variant='caption'>
                          {categoryLabel(pair.issue)}
                          {' · '}
                          {pair.issue.issue_id}
                        </Typography>
                        {!!pair.parent && (
                          <>
                            <ArrowForwardIcon
                              sx={{ fontSize: 14, color: 'text.secondary' }}
                            />
                            <Typography variant='caption'>
                              {pair.parent.issue_id}
                            </Typography>
                          </>
                        )}
                      </Stack>

                      {!pair.parent && (
                        <Typography
                          variant='caption'
                          color='error.dark'
                          sx={{ pl: 1.5 }}
                        >
                          {REASON_LABELS[pair.reason!]}
                        </Typography>
                      )}
                    </Stack>
                  ))}
              </Stack>
            ))}

          {!savable.length && (
            <Typography variant='caption' color='error.dark'>
              {__(
                'Duplikát sa dá označiť len z nálezu, ktorý má primárny podnet v tej istej kategórii. Vyriešený nález sa duplikátom už nestane.',
              )}
            </Typography>
          )}
        </Stack>
      </Stack>
    </ConfirmationDialog>
  );
};

export default AdminReportsMergeModal;
